"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.fromValue = exports._ionValueFromJsValue = exports._domConstructorFor = exports.Sequence = exports.List = exports.Struct = void 0;
const jsbi_1 = __importDefault(require("jsbi"));
const IonTypes_1 = require("../IonTypes");
const util_1 = require("../util");
const Value_1 = require("./Value");
const Integer_1 = require("./Integer");
const Blob_1 = require("./Blob");
const Decimal_1 = require("./Decimal");
const String_1 = require("./String");
const Timestamp_1 = require("./Timestamp");
const Boolean_1 = require("./Boolean");
const IonDecimal_1 = require("../IonDecimal");
const IonTimestamp_1 = require("../IonTimestamp");
const FromJsConstructor_1 = require("./FromJsConstructor");
/**
 * Represents a struct[1] value in an Ion stream.
 *
 * Struct fields can be accessed as properties on this object. For example:
 *
 *    let s: any = ion.load('{foo: 1, bar: 2, baz: qux::3}');
 *    assert.equal(6, s.foo + s['bar'] + s.baz);
 *
 * If a field in the struct has the same name as a method on the Struct class, attempting to access that property
 * will always resolve to the method.
 *
 *     let s: any = ion.load('{fieldNames: ["foo", "bar", "baz"]}'); // Conflicts with Struct#fieldNames()
 *     assert.deepEqual(s.fieldNames, ["foo", "bar", "baz"]); // Fails; `s.fieldNames` is a method
 *     assert.deepEqual(s.fieldNames(), ["fieldNames"]); // Passes
 *
 * Unlike direct property accesses, Struct's get() method will only resolve to fields.
 *
 *     let s: any = ion.load('{fieldNames: ["foo", "bar", "baz"]}'); // Conflicts with Struct#fieldNames()
 *     assert.equal(s.get('fieldNames', 0).stringValue(), "foo"); // Passes
 *
 * [1] http://amzn.github.io/ion-docs/docs/spec.html#struct
 */
class Struct extends (0, Value_1.Value)(Object, IonTypes_1.IonTypes.STRUCT, FromJsConstructor_1.FromJsConstructor.NONE) {
    /**
     * Constructor.
     * @param fields        An iterator of field name/value pairs to represent as a struct.
     * @param annotations   An optional array of strings to associate with this null value.
     */
    constructor(fields, annotations = []) {
        super();
        /*
         * Stores the string/Value pairs that represent the fields of the struct. These values are stored
         * separately to allow field names that would otherwise collide with public properties from the Struct class
         * itself to be stored. (e.g. 'stringValue', 'fields', or 'elements')
         */
        this._fields = Object.create(null);
        // Struct fields will always be stored as (fieldName, fieldValues) => (string, Value[])
        for (const [fieldName, fieldValue] of fields) {
            this._fields[fieldName] =
                fieldValue instanceof Value_1.Value ? [fieldValue] : fieldValue;
        }
        this._setAnnotations(annotations);
        // Construct a Proxy that will prevent user-defined fields from shadowing public properties.
        return new Proxy(this, {
            // All values set by the user are stored in `this._fields` to avoid
            // potentially overwriting Struct methods.
            set: function (target, name, value) {
                target._fields[name] = [value];
                return true; // Indicates that the assignment succeeded
            },
            get: function (target, name) {
                // Property accesses will look for matching Struct API properties before
                // looking for a matching field of the same name.
                if (name in target) {
                    return target[name];
                }
                let length = target._fields[name] !== undefined ? target._fields[name].length : -1;
                if (length === -1) {
                    return target._fields[name];
                }
                return target._fields[name][length - 1];
            },
            deleteProperty: function (target, name) {
                // Property is deleted only if it's in _field Collection
                if (name in target._fields) {
                    delete target._fields[name];
                }
                // Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/delete#Return_value
                return true;
            },
        });
    }
    get(...pathElements) {
        if (pathElements.length === 0) {
            throw new Error("Value#get requires at least one parameter.");
        }
        const [pathHead, ...pathTail] = pathElements;
        if (typeof pathHead !== "string") {
            throw new Error(`Cannot index into a struct with a ${typeof pathHead}.`);
        }
        const child = this._fields[pathHead];
        if (child === undefined) {
            return null;
        }
        if (pathTail.length === 0) {
            return child[child.length - 1];
        }
        return child[child.length - 1].get(...pathTail);
    }
    getAll(...pathElements) {
        if (pathElements.length === 0) {
            throw new Error("Value#get requires at least one parameter.");
        }
        const [pathHead, ...pathTail] = pathElements;
        if (typeof pathHead !== "string") {
            throw new Error(`Cannot index into a struct with a ${typeof pathHead}.`);
        }
        const child = this._fields[pathHead];
        if (child === undefined) {
            return null;
        }
        if (pathTail.length === 0) {
            return child;
        }
        let values = [];
        child.forEach((value) => values.push(...value.getAll(...pathTail)));
        return values;
    }
    fieldNames() {
        return Object.keys(this._fields);
    }
    allFields() {
        return Object.entries(this._fields);
    }
    fields() {
        let singleValueFields = Object.create(null);
        for (const [fieldName, values] of this.allFields()) {
            singleValueFields[fieldName] = values[values.length - 1];
        }
        return Object.entries(singleValueFields);
    }
    elements() {
        let singleValueFields = Object.create(null);
        for (const [fieldName, values] of this.allFields()) {
            singleValueFields[fieldName] = values[values.length - 1];
        }
        return Object.values(singleValueFields);
    }
    [Symbol.iterator]() {
        return this.fields()[Symbol.iterator]();
    }
    toString() {
        return ("{" +
            [...this.allFields()]
                .map(([name, value]) => name + ": " + value)
                .join(", ") +
            "}");
    }
    writeTo(writer) {
        writer.setAnnotations(this.getAnnotations());
        writer.stepIn(IonTypes_1.IonTypes.STRUCT);
        for (const [fieldName, values] of this.allFields()) {
            for (let value of values) {
                writer.writeFieldName(fieldName);
                value.writeTo(writer);
            }
        }
        writer.stepOut();
    }
    deleteField(name) {
        if (name in this._fields) {
            delete this._fields[name];
            return true;
        }
        return false;
    }
    toJSON() {
        let normalizedFields = Object.create(null);
        for (const [key, value] of this.fields()) {
            normalizedFields[key] = value;
        }
        return normalizedFields;
    }
    static _fromJsValue(jsValue, annotations) {
        if (!(jsValue instanceof Object)) {
            throw new Error(`Cannot create a dom.Struct from: ${jsValue.toString()}`);
        }
        const fields = Object.entries(jsValue).map(([key, value]) => [key, [fromValue(value)]]);
        return new this(fields, annotations);
    }
    _valueEquals(other, options = {
        epsilon: null,
        ignoreAnnotations: false,
        ignoreTimestampPrecision: false,
        onlyCompareIon: true,
    }) {
        let isSupportedType = false;
        let valueToCompare = null;
        // if the provided value is an ion.dom.Struct instance.
        if (other instanceof Struct) {
            isSupportedType = true;
            valueToCompare = other.allFields();
        }
        else if (!options.onlyCompareIon) {
            // We will consider other Struct-ish types
            if (typeof other === "object" || other instanceof Object) {
                isSupportedType = true;
                valueToCompare = fromValue(other).allFields();
            }
        }
        if (!isSupportedType) {
            return false;
        }
        if (this.allFields().length !== valueToCompare.length) {
            return false;
        }
        let matchFound = true;
        const paired = new Array(valueToCompare.length);
        for (let i = 0; matchFound && i < this.allFields().length; i++) {
            matchFound = false;
            for (let j = 0; !matchFound && j < valueToCompare.length; j++) {
                if (!paired[j]) {
                    const child = this.allFields()[i];
                    const expectedChild = valueToCompare[j];
                    matchFound =
                        child[0] === expectedChild[0] &&
                            this._ionValueEquals(child[1].sort(), expectedChild[1].sort(), options);
                    if (matchFound) {
                        paired[j] = true;
                    }
                }
            }
        }
        // set matchFound to the first pair that didn't find a matching field if any
        for (let i = 0; i < paired.length; i++) {
            if (!paired[i]) {
                matchFound = false;
                break;
            }
        }
        return matchFound;
    }
    // helper function for comparison of all values of a field
    _ionValueEquals(child, expectedChild, options) {
        if (child.length !== expectedChild.length) {
            return false;
        }
        for (let i = 0; i < child.length; i++) {
            if (options.onlyCompareIon) {
                if (!child[i].ionEquals(expectedChild[i], options)) {
                    return false;
                }
            }
            else {
                if (!child[i].equals(expectedChild[i])) {
                    return false;
                }
            }
        }
        return true;
    }
}
exports.Struct = Struct;
/**
 * Represents a list value in an Ion stream.
 *
 * [1] http://amzn.github.io/ion-docs/docs/spec.html#list
 */
class List extends Sequence(IonTypes_1.IonTypes.LIST) {
    /**
     * Constructor.
     * @param children      Values that will be contained in the new list.
     * @param annotations   An optional array of strings to associate with the items in `children`.
     */
    constructor(children, annotations = []) {
        super(children, annotations);
    }
}
exports.List = List;
/**
 * This mixin constructs a new class that:
 * - Extends `DomValue`
 * - Extends `Array`
 * - Has the specified `IonType`.
 *
 * In practice, serves as a common base class for `List` and `SExpression`.
 *
 * @param ionType   The IonType to associate with the new DomValue subclass.
 * @constructor
 * @private
 */
function Sequence(ionType) {
    return class extends (0, Value_1.Value)(Array, ionType, FromJsConstructor_1.FromJsConstructor.NONE) {
        constructor(children, annotations = []) {
            super();
            for (const child of children) {
                this.push(child);
            }
            this._setAnnotations(annotations);
        }
        get(...pathElements) {
            if (pathElements.length === 0) {
                throw new Error("Value#get requires at least one parameter.");
            }
            const [pathHead, ...pathTail] = pathElements;
            if (typeof pathHead !== "number") {
                throw new Error(`Cannot index into a ${this.getType().name} with a ${typeof pathHead}.`);
            }
            const children = this;
            const maybeChild = children[pathHead];
            const child = maybeChild === undefined ? null : maybeChild;
            if (pathTail.length === 0 || child === null) {
                return child;
            }
            return child.get(...pathTail);
        }
        elements() {
            return Object.values(this);
        }
        toString() {
            return "[" + this.join(", ") + "]";
        }
        static _fromJsValue(jsValue, annotations) {
            if (!(jsValue instanceof Array)) {
                throw new Error(`Cannot create a ${this.name} from: ${jsValue.toString()}`);
            }
            const children = jsValue.map((child) => fromValue(child));
            return new this(children, annotations);
        }
        writeTo(writer) {
            writer.setAnnotations(this.getAnnotations());
            writer.stepIn(ionType);
            for (const child of this) {
                child.writeTo(writer);
            }
            writer.stepOut();
        }
        _valueEquals(other, options = {
            epsilon: null,
            ignoreAnnotations: false,
            ignoreTimestampPrecision: false,
            onlyCompareIon: true,
        }) {
            let isSupportedType = false;
            let valueToCompare = null;
            if (options.onlyCompareIon) {
                // `compareOnlyIon` requires that the provided value be an ion.dom.Sequence instance.
                if (other.getType() === IonTypes_1.IonTypes.LIST ||
                    other.getType() === IonTypes_1.IonTypes.SEXP) {
                    isSupportedType = true;
                    valueToCompare = other.elements();
                }
            }
            else {
                // We will consider other Sequence-ish types
                if (other instanceof Array) {
                    isSupportedType = true;
                    valueToCompare = other;
                }
            }
            if (!isSupportedType) {
                return false;
            }
            let actualSequence = this.elements();
            let expectedSequence = valueToCompare;
            if (actualSequence.length !== expectedSequence.length) {
                return false;
            }
            for (let i = 0; i < actualSequence.length; i++) {
                if (options.onlyCompareIon) {
                    if (!actualSequence[i].ionEquals(expectedSequence[i], options)) {
                        return false;
                    }
                }
                else {
                    if (!actualSequence[i].equals(expectedSequence[i])) {
                        return false;
                    }
                }
            }
            return true;
        }
    };
}
exports.Sequence = Sequence;
// When Typescript is compiling this code, dom/index.ts depends on Value.ts which in turn
// depends on this file. This means that 'dom' is still being defined and we cannot yet
// reference any of the Value subtypes (dom.Integer, dom.String, etc). We can sidestep this
// issue by lazily initializing this mapping, deferring any reference to those types until
// they are first used.
let _domTypesByIonType = null;
// Lazily initializes our IonType -> Dom type constructor mapping.
function _getDomTypesByIonTypeMap() {
    if (_domTypesByIonType === null) {
        // Clob, SExpression, and Symbol are not included here as _inferType always
        // favors Blob, List, and String respectively.
        _domTypesByIonType = new Map([
            [IonTypes_1.IonTypes.NULL, FromJsConstructor_1.Null],
            [IonTypes_1.IonTypes.BOOL, Boolean_1.Boolean],
            [IonTypes_1.IonTypes.INT, Integer_1.Integer],
            [IonTypes_1.IonTypes.FLOAT, Decimal_1.Float],
            [IonTypes_1.IonTypes.DECIMAL, Decimal_1.Decimal],
            [IonTypes_1.IonTypes.TIMESTAMP, Timestamp_1.Timestamp],
            [IonTypes_1.IonTypes.STRING, String_1.String],
            [IonTypes_1.IonTypes.BLOB, Blob_1.Blob],
            [IonTypes_1.IonTypes.LIST, List],
            [IonTypes_1.IonTypes.STRUCT, Struct],
        ]);
    }
    return _domTypesByIonType;
}
// Returns a dom type constructor for the provided IonType.
// This function is exported to assist in unit testing but is not visible at the library level.
function _domConstructorFor(ionType) {
    const domConstructor = _getDomTypesByIonTypeMap().get(ionType);
    if (!(0, util_1._hasValue)(domConstructor)) {
        throw new Error(`No dom type constructor was found for Ion type ${ionType.name}`);
    }
    return domConstructor;
}
exports._domConstructorFor = _domConstructorFor;
// Examines the provided JS value and returns the IonType best suited to represent it.
// This function will never return Clob, SExpression, and Symbol; it will always
// favor Blob, List, and String respectively.
function _inferType(value) {
    if (value === undefined) {
        throw new Error("Cannot create an Ion value from `undefined`.");
    }
    if (value === null) {
        return IonTypes_1.IonTypes.NULL;
    }
    const valueType = typeof value;
    switch (valueType) {
        case "string":
            return IonTypes_1.IonTypes.STRING;
        case "number":
            return Number.isInteger(value) ? IonTypes_1.IonTypes.INT : IonTypes_1.IonTypes.FLOAT;
        case "boolean":
            return IonTypes_1.IonTypes.BOOL;
        case "object":
            break;
        case "bigint":
            break;
        // ^-- We `break` here to defer handling native bigints to the 'valueof JSBI' test below,
        // which will handle native bigints correctly if Babel is being used to convert JSBI
        // logic to native bigint logic.
        // TODO: Javascript symbols are not a 1:1 match for Ion symbols, but we may wish
        //       to support them in Value.from() anyway.
        // case "symbol":
        default:
            throw new Error(`Value.from() does not support the JS primitive type ${valueType}.`);
    }
    // TODO: Use the code below to create an easy-to-understand _babelJsbiIsEnabled() test.
    // If `value` is a native bigint but we're not using Babel for native bigint support, we need to
    // throw an Error.
    //
    // If Babel is being used, this test becomes:
    //      typeof(value) === 'bigint'
    // which will return true for native bigints, leading to the creation of an Integer.
    //
    // If Babel is not being used, then this remains a JSBI instanceof test, which will
    // return false for native bigints.
    if (value instanceof jsbi_1.default) {
        return IonTypes_1.IonTypes.INT;
    }
    else if (typeof value === "bigint") {
        // If Babel is being used, this check will be identical to the one above it and it will
        // never be reached. If Babel is not being used, this test will return true for native
        // bigints (which aren't supported without Babel) while allowing other Object types
        // to be processed below.
        throw new Error("bigints are not supported without using Babel for JSBI compilation.");
    }
    if (value instanceof Number) {
        return Number.isInteger(value.valueOf()) ? IonTypes_1.IonTypes.INT : IonTypes_1.IonTypes.FLOAT;
    }
    if (value instanceof Boolean) {
        return IonTypes_1.IonTypes.BOOL;
    }
    if (value instanceof String) {
        return IonTypes_1.IonTypes.STRING;
    }
    if (value instanceof IonDecimal_1.Decimal) {
        return IonTypes_1.IonTypes.DECIMAL;
    }
    if (value instanceof Date || value instanceof IonTimestamp_1.Timestamp) {
        return IonTypes_1.IonTypes.TIMESTAMP;
    }
    if (value instanceof Uint8Array) {
        return IonTypes_1.IonTypes.BLOB;
    }
    if (value instanceof Array) {
        return IonTypes_1.IonTypes.LIST;
    }
    return IonTypes_1.IonTypes.STRUCT;
}
// Inspects the provided JS value and constructs a new instance of the appropriate Ion
// type using that value.
function _ionValueFromJsValue(value, annotations = []) {
    const ionType = _inferType(value);
    const ionTypeConstructor = _domConstructorFor(ionType);
    return ionTypeConstructor._fromJsValue(value, annotations);
}
exports._ionValueFromJsValue = _ionValueFromJsValue;
/**
 * Constructs a dom.Value from the provided Javascript value using the following type mappings:
 *
 * JS Type    | dom.Value subclass
 * -----------------------------
 * null       | dom.Null
 * boolean    | dom.Boolean
 * number     | dom.Integer or dom.Float
 * BigInt     | dom.Integer
 * string     | dom.String
 * Decimal    | dom.Decimal
 * Date       | dom.Timestamp
 * Timestamp  | dom.Timestamp
 * Uint8Array | dom.Blob
 * Array      | dom.List
 * Object     | dom.Struct
 *
 * Other input types (including 'undefined') are not supported and will throw an Error.
 *
 * If the input type is an Array or Object, this method will also convert each nested javascript
 * values into a dom.Value.
 *
 * @param value         A javascript value to convert into an Ion dom.Value.
 * @param annotations   An optional array of strings to associate with the newly created dom.Value.
 *                      These annotations will NOT be associated with any nested dom.Values.
 */
function fromValue(value, annotations) {
    if (value instanceof Value_1.Value) {
        if ((0, util_1._hasValue)(annotations)) {
            // TODO: This should probably be supported, but will require a clone(annotations?: string[])
            //       API (or similar) to be added to Value subclasses.
            throw new Error("Value.from() does not support overriding the annotations on a dom.Value" +
                " passed as an argument.");
        }
        return value;
    }
    return _ionValueFromJsValue(value, annotations);
}
exports.fromValue = fromValue;
